/**
 * @fileoverview Modal component for creating and editing Tug of War (Estira y Afloja) themes.
 * Supports 5 complete navigation tabs: General & Cover, Game Scene & Mud/Rope, Team 1 (Left), Team 2 (Right), and Mobile Fan View.
 * Implements Drag & Drop media uploads, instant image preview, defensive form validation, and feedback via useFloatMessage.
 * 
 * @module components/Admin/Games/TugOfWar/TugOfWarThemeModal
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFloatMessage, MESSAGE_TYPE } from '../../../Providers/FloatMessage';
import { UploadMedia, SaveTheme, ResolveMediaUrl } from '../../../../utilities/BL/TugOfWar';

/**
 * Resuelve y sanitiza la URL absoluta para la previsualización de archivos multimedia.
 * 
 * @param {string|null} mediaPath - Ruta relativa o URL absoluta.
 * @returns {string} URL formateada para previsualización.
 */
export const getFullAssetUrl = (mediaPath) => {
    return ResolveMediaUrl(mediaPath);
};

/**
 * Componente Drag & Drop Reutilizable para campos individuales de multimedia.
 * 
 * @function DropzoneField
 * @param {Object} props - Propiedades del componente.
 * @param {string} props.label - Etiqueta descriptiva del campo.
 * @param {string} props.fieldName - Clave del campo en el estado del formulario.
 * @param {string} props.category - Categoría del asset para optimización.
 * @param {string} [props.value=''] - URL actual del recurso multimedia.
 * @param {Function} props.onUpload - Callback para procesar la subida del archivo.
 * @param {Function} props.onRemove - Callback para limpiar el recurso seleccionado.
 * @param {boolean} [props.isUploading=false] - Indicador de subida activa.
 * @param {string} [props.helpText=''] - Texto de ayuda contextual.
 * @param {string} [props.aspectHint=''] - Recomendación de formato o resolución.
 * @param {string} [props.accept='image/*'] - Tipos MIME aceptados.
 * @returns {React.ReactElement} Campo de carga con drag & drop y preview.
 */
const DropzoneField = ({
    label,
    fieldName,
    category,
    value = '',
    onUpload,
    onRemove,
    isUploading = false,
    helpText = '',
    aspectHint = '',
    accept = 'image/*'
}) => {
    const [isDragHoverActive, setIsDragHoverActive] = useState(false);
    const inputElementId = `tugofwar-file-input-${fieldName}`;

    const handleDragOver = (dragEvent) => {
        dragEvent.preventDefault();
        dragEvent.stopPropagation();
        setIsDragHoverActive(true);
    };

    const handleDragLeave = (dragEvent) => {
        dragEvent.preventDefault();
        dragEvent.stopPropagation();
        setIsDragHoverActive(false);
    };

    const handleDrop = (dropEvent) => {
        dropEvent.preventDefault();
        dropEvent.stopPropagation();
        setIsDragHoverActive(false);
        const droppedFiles = dropEvent.dataTransfer.files;
        if (droppedFiles && droppedFiles.length > 0) {
            onUpload(droppedFiles[0], category, fieldName);
        }
    };

    const handleNativeFileChange = (changeEvent) => {
        const selectedFiles = changeEvent.target.files;
        if (selectedFiles && selectedFiles.length > 0) {
            onUpload(selectedFiles[0], category, fieldName);
            changeEvent.target.value = '';
        }
    };

    return (
        <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
                {label && (
                    <label className="form-label fw-bold text-dark small mb-0">
                        {label}
                    </label>
                )}
                {aspectHint && (
                    <span className="badge bg-light text-muted border small">
                        {aspectHint}
                    </span>
                )}
            </div>

            <div
                className={`border border-2 rounded-3 p-3 text-center position-relative transition-all d-flex flex-column align-items-center justify-content-center ${
                    isDragHoverActive
                        ? 'border-primary bg-primary-subtle shadow-sm'
                        : 'border-secondary-subtle bg-light'
                }`}
                style={{
                    borderStyle: 'dashed',
                    minHeight: '105px',
                    cursor: isUploading ? 'not-allowed' : 'pointer'
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => {
                    if (!isUploading) {
                        const inputEl = document.getElementById(inputElementId);
                        if (inputEl) inputEl.click();
                    }
                }}
            >
                <input
                    id={inputElementId}
                    type="file"
                    className="d-none"
                    accept={accept}
                    onChange={handleNativeFileChange}
                    disabled={isUploading}
                />

                {isUploading ? (
                    <div className="py-2 d-flex flex-column align-items-center">
                        <div className="spinner-border spinner-border-sm text-primary mb-2" role="status"></div>
                        <span className="small text-primary fw-bold">Subiendo y optimizando archivo...</span>
                    </div>
                ) : value ? (
                    <div
                        className="position-relative d-flex flex-column align-items-center w-100"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={getFullAssetUrl(value)}
                            alt="Vista previa"
                            className="img-thumbnail shadow-sm bg-white mb-2"
                            style={{
                                maxHeight: '85px',
                                maxWidth: '100%',
                                objectFit: 'contain'
                            }}
                            onError={(errorEvent) => {
                                errorEvent.target.style.display = 'none';
                            }}
                        />
                        <div className="d-flex gap-2 align-items-center">
                            <span
                                className="small text-truncate text-muted"
                                style={{ maxWidth: '180px' }}
                                title={value}
                            >
                                {value.split('/').pop()}
                            </span>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger py-0 px-2"
                                title="Eliminar archivo"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRemove(fieldName);
                                }}
                            >
                                <i className="bi bi-trash3"></i>
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary py-0 px-2"
                                title="Reemplazar archivo"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const inputEl = document.getElementById(inputElementId);
                                    if (inputEl) inputEl.click();
                                }}
                            >
                                <i className="bi bi-arrow-repeat"></i>
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <i className="bi bi-cloud-arrow-up text-primary fs-3 d-block mb-1"></i>
                        <span className="small text-muted d-block">
                            Arrastra aquí tu archivo o haz <strong className="text-primary">clic para buscar</strong>
                        </span>
                        {helpText && (
                            <small className="text-secondary d-block mt-1 font-monospace" style={{ fontSize: '0.75rem' }}>
                                {helpText}
                            </small>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

DropzoneField.propTypes = {
    label: PropTypes.string,
    fieldName: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    value: PropTypes.string,
    onUpload: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    isUploading: PropTypes.bool,
    helpText: PropTypes.string,
    aspectHint: PropTypes.string,
    accept: PropTypes.string
};

/**
 * Plantilla de estructura inicial para temas visuales de Tug of War.
 */
const DEFAULT_THEME_STATE = {
    id: 0,
    name: '',
    brandLogoUrl: '',
    sponsorLogoUrl: '',
    coverTitle: '¡ESTIRA Y AFLOJA!',
    fontFamily: 'Bangers',

    // Pestaña 1: Portada
    coverBackgroundType: 'color',
    coverBackgroundColor: '#0f172a',
    coverBackgroundUrl: '',
    coverTextColor: '#ffffff',

    // Pestaña 2: Escenario, Cuerda y Lodo
    gameBackgroundType: 'color',
    gameBackgroundColor: '#020617',
    gameBackgroundUrl: '',
    gameTextColor: '#ffffff',
    ropeTextureUrl: '',
    mudPuddleColor: '#451a03',
    mudPuddleUrl: '',

    // Pestaña 3: Equipo 1 (Izquierda)
    player1Name: 'EQUIPO 1',
    player1Color: '#0284c7',
    player1Phase1Avatar: '',
    player1Phase2Avatar: '',
    player1Phase3Avatar: '',
    player1LoserAvatar: '',
    player1WinnerAvatar: '',

    // Pestaña 4: Equipo 2 (Derecha)
    player2Name: 'EQUIPO 2',
    player2Color: '#ef4444',
    player2Phase1Avatar: '',
    player2Phase2Avatar: '',
    player2Phase3Avatar: '',
    player2LoserAvatar: '',
    player2WinnerAvatar: '',

    // Pestaña 5: Vista Móvil Aficionado
    fanBackgroundType: 'color',
    fanBackgroundColor: '#0f172a',
    fanBackgroundUrl: '',
    fanTextColor: '#ffffff'
};

/**
 * Catálogo de fuentes tipográficas recomendadas para el juego.
 */
const FONT_FAMILIES = [
    { label: 'Bangers (Comic / Acción)', value: 'Bangers' },
    { label: 'Montserrat (Moderno / Limpio)', value: 'Montserrat' },
    { label: 'Anton (Impacto / Deportivo)', value: 'Anton' },
    { label: 'Bebas Neue (Titular / Alto)', value: 'Bebas Neue' },
    { label: 'Oswald (Condensado)', value: 'Oswald' },
    { label: 'Impact (Clásico Estadio)', value: 'Impact' },
    { label: 'Fredoka One (Caricatura / Dinámico)', value: 'Fredoka One' },
    { label: 'Roboto (Neutro)', value: 'Roboto' }
];

/**
 * Modal para la creación, edición y personalización de temas de Estira y Afloja (Tug of War).
 * 
 * @function TugOfWarThemeModal
 * @param {Object} props - Propiedades del modal.
 * @param {boolean} [props.isOpen=false] - Control de visibilidad del modal.
 * @param {boolean} [props.show=false] - Alias de compatibilidad para isOpen.
 * @param {Function} [props.onCloseModal] - Callback para cerrar el modal.
 * @param {Function} [props.onClose] - Alias de compatibilidad para onCloseModal.
 * @param {Object|null} [props.initialThemeData=null] - Datos del tema a editar.
 * @param {Object|null} [props.themeToEdit=null] - Alias de compatibilidad para initialThemeData.
 * @param {Function} [props.onThemeSavedSuccessfully] - Callback invocado tras guardar exitosamente.
 * @param {Function} [props.onThemeSaved] - Alias de compatibilidad para onThemeSavedSuccessfully.
 * @param {Function} [props.onSave] - Alias de compatibilidad para onThemeSavedSuccessfully.
 * @param {string|null} [props.roomId=null] - Identificador GUID de la sala activa.
 * @param {string|null} [props.operatorId=null] - Identificador GUID del operador.
 * @returns {React.ReactElement|null} Modal interactivo de administración de temas.
 */
export const TugOfWarThemeModal = ({
    isOpen: propIsOpen,
    show,
    onCloseModal: propOnCloseModal,
    onClose,
    initialThemeData: propInitialThemeData,
    themeToEdit,
    onThemeSavedSuccessfully: propOnThemeSavedSuccessfully,
    onThemeSaved,
    onSave,
    roomId = null,
    operatorId = null
}) => {
    const isOpen = propIsOpen !== undefined ? propIsOpen : Boolean(show);
    const handleClose = propOnCloseModal || onClose || (() => {});
    const initialData = propInitialThemeData || themeToEdit || null;
    const handleThemeSaved = propOnThemeSavedSuccessfully || onThemeSaved || onSave || (() => {});

    const { showMessage } = useFloatMessage();
    const [activeTabKey, setActiveTabKey] = useState('general');
    const [isSavingInProgress, setIsSavingInProgress] = useState(false);
    const [activeUploadFieldKey, setActiveUploadFieldKey] = useState('');
    const [themeFormState, setThemeFormState] = useState(DEFAULT_THEME_STATE);

    useEffect(() => {
        if (initialData && (initialData.id > 0 || initialData.themeId > 0)) {
            setThemeFormState({
                ...DEFAULT_THEME_STATE,
                ...initialData,
                id: initialData.id || initialData.themeId || 0,
                player1LoserAvatar: initialData.player1LoserAvatar || initialData.player1Loser || '',
                player1WinnerAvatar: initialData.player1WinnerAvatar || initialData.player1Winner || '',
                player2LoserAvatar: initialData.player2LoserAvatar || initialData.player2Loser || '',
                player2WinnerAvatar: initialData.player2WinnerAvatar || initialData.player2Winner || ''
            });
        } else {
            setThemeFormState(DEFAULT_THEME_STATE);
        }
        setActiveTabKey('general');
    }, [initialData, isOpen]);

    if (!isOpen) return null;

    /**
     * Actualiza una propiedad del estado del formulario.
     */
    const handleFieldChange = (fieldName, fieldValue) => {
        setThemeFormState((previousState) => ({
            ...previousState,
            [fieldName]: fieldValue
        }));
    };

    /**
     * Limpia el valor de un campo multimedia.
     */
    const handleRemoveMediaField = (fieldName) => {
        handleFieldChange(fieldName, '');
    };

    /**
     * Maneja la subida de un archivo individual mediante UploadMedia.
     */
    const handleSingleFileUpload = async (fileObject, mediaCategory, targetFieldName) => {
        if (!fileObject) return;

        setActiveUploadFieldKey(targetFieldName);
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Subiendo y optimizando archivo...' });

        try {
            const uploadResponse = await UploadMedia(fileObject, mediaCategory, roomId);
            const resolvedUrl = uploadResponse.url || uploadResponse.data?.url || uploadResponse.data?.filePath || '';

            if (uploadResponse.success && resolvedUrl) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Archivo cargado con éxito.' });
                handleFieldChange(targetFieldName, resolvedUrl);
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: uploadResponse.message || 'Error al subir el archivo multimedia.'
                });
            }
        } catch (uploadException) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado durante la subida de archivo.' });
        } finally {
            setActiveUploadFieldKey('');
        }
    };

    /**
     * Envía el formulario para crear o actualizar el tema visual en el backend.
     */
    const handleFormSubmit = async (formEvent) => {
        formEvent.preventDefault();

        const trimmedName = themeFormState.name ? themeFormState.name.trim() : '';
        if (!trimmedName) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'El nombre del tema es obligatorio.' });
            setActiveTabKey('general');
            return;
        }

        setIsSavingInProgress(true);
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Guardando configuración del tema...' });

        try {
            const payloadToSave = {
                ...themeFormState,
                name: trimmedName,
                roomId: themeFormState.roomId || roomId || null,
                operatorId: themeFormState.operatorId || operatorId || null
            };

            const saveResponse = await SaveTheme(payloadToSave, roomId, operatorId);
            if (saveResponse.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `Tema "${trimmedName}" guardado exitosamente.`
                });
                handleThemeSaved(saveResponse.data || payloadToSave);
                handleClose();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: saveResponse.message || 'Error al guardar el tema.'
                });
            }
        } catch (saveException) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al persistir el tema.' });
        } finally {
            setIsSavingInProgress(false);
        }
    };

    return (
        <>
            <div className="cModal__Background" onClick={handleClose}></div>
            <div className="cModal__Container" style={{ zIndex: 99999, maxWidth: '980px', width: '95vw' }}>
                <div
                    className="cModal__Container__Content bg-white rounded-4 shadow-lg p-0 overflow-hidden text-dark position-relative"
                    style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
                >
                    {/* Modal Header */}
                    <div className="d-flex justify-content-between align-items-center p-3 px-4 bg-dark text-white border-bottom border-secondary">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-arrows-collapse text-warning fs-4"></i>
                            <h5 className="m-0 fw-bold">
                                {themeFormState.id > 0
                                    ? `Editar Tema: ${themeFormState.name}`
                                    : 'Crear Nuevo Tema - Estira y Afloja (Tug of War)'}
                            </h5>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-light rounded-circle"
                            onClick={handleClose}
                            title="Cerrar modal"
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>

                    {/* Navigation Tabs Header */}
                    <div className="bg-light border-bottom px-3 pt-2">
                        <ul className="nav nav-tabs border-0 flex-nowrap overflow-x-auto">
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${
                                        activeTabKey === 'general' ? 'active text-primary border-bottom-0' : 'text-secondary'
                                    }`}
                                    onClick={() => setActiveTabKey('general')}
                                >
                                    <i className="bi bi-sliders me-1"></i> 1. General y Portada
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${
                                        activeTabKey === 'game' ? 'active text-primary border-bottom-0' : 'text-secondary'
                                    }`}
                                    onClick={() => setActiveTabKey('game')}
                                >
                                    <i className="bi bi-aspect-ratio me-1"></i> 2. Escenario, Cuerda y Lodo
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${
                                        activeTabKey === 'team1' ? 'active text-primary border-bottom-0' : 'text-secondary'
                                    }`}
                                    onClick={() => setActiveTabKey('team1')}
                                >
                                    <i className="bi bi-shield-shaded text-info me-1"></i> 3. Equipo 1 (Izquierda)
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${
                                        activeTabKey === 'team2' ? 'active text-primary border-bottom-0' : 'text-secondary'
                                    }`}
                                    onClick={() => setActiveTabKey('team2')}
                                >
                                    <i className="bi bi-shield-shaded text-danger me-1"></i> 4. Equipo 2 (Derecha)
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${
                                        activeTabKey === 'fan' ? 'active text-primary border-bottom-0' : 'text-secondary'
                                    }`}
                                    onClick={() => setActiveTabKey('fan')}
                                >
                                    <i className="bi bi-phone me-1"></i> 5. Vista Móvil Aficionado
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* Scrollable Tab Body */}
                    <form onSubmit={handleFormSubmit} className="d-flex flex-column flex-grow-1 overflow-hidden m-0 p-0">
                        <div className="p-4 overflow-y-auto flex-grow-1" style={{ maxHeight: 'calc(90vh - 180px)' }}>
                            {/* ============================================================ */}
                            {/* PESTAÑA 1: GENERAL Y PORTADA                                 */}
                            {/* ============================================================ */}
                            {activeTabKey === 'general' && (
                                <div className="row g-3">
                                    <div className="col-12">
                                        <label className="form-label fw-bold">
                                            Nombre del Tema <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="ej. Estira y Afloja Clásico, Duelo Titanes, Noche de Leyendas..."
                                            value={themeFormState.name}
                                            onChange={(e) => handleFieldChange('name', e.target.value)}
                                            required
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <DropzoneField
                                            label="Logo de Marca / Club (Brand)"
                                            fieldName="brandLogoUrl"
                                            category="logo"
                                            value={themeFormState.brandLogoUrl}
                                            onUpload={handleSingleFileUpload}
                                            onRemove={handleRemoveMediaField}
                                            isUploading={activeUploadFieldKey === 'brandLogoUrl'}
                                            helpText="Logo institucional o del club local (PNG transparente)"
                                            aspectHint="PNG Transparente"
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <DropzoneField
                                            label="Logo del Patrocinador Oficial"
                                            fieldName="sponsorLogoUrl"
                                            category="logo"
                                            value={themeFormState.sponsorLogoUrl}
                                            onUpload={handleSingleFileUpload}
                                            onRemove={handleRemoveMediaField}
                                            isUploading={activeUploadFieldKey === 'sponsorLogoUrl'}
                                            helpText="Logo comercial o sponsor del evento (PNG transparente)"
                                            aspectHint="PNG Transparente"
                                        />
                                    </div>

                                    <div className="col-12">
                                        <hr className="my-2" />
                                        <h6 className="fw-bold text-primary mb-2">
                                            <i className="bi bi-image me-1"></i> Personalización de la Portada Promocional
                                        </h6>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Título de Portada</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="¡ESTIRA Y AFLOJA!"
                                            value={themeFormState.coverTitle || ''}
                                            onChange={(e) => handleFieldChange('coverTitle', e.target.value)}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Tipografía Principal (Font Family)</label>
                                        <select
                                            className="form-select"
                                            value={themeFormState.fontFamily || 'Bangers'}
                                            onChange={(e) => handleFieldChange('fontFamily', e.target.value)}
                                        >
                                            {FONT_FAMILIES.map((font) => (
                                                <option key={font.value} value={font.value}>
                                                    {font.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Tipo de Fondo de Portada</label>
                                        <select
                                            className="form-select"
                                            value={themeFormState.coverBackgroundType || 'color'}
                                            onChange={(e) => handleFieldChange('coverBackgroundType', e.target.value)}
                                        >
                                            <option value="color">Color Sólido</option>
                                            <option value="image">Imagen / Gráfico</option>
                                        </select>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Color de Fondo de Portada</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.coverBackgroundColor || '#0f172a'}
                                                onChange={(e) => handleFieldChange('coverBackgroundColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase font-monospace"
                                                value={themeFormState.coverBackgroundColor || '#0f172a'}
                                                onChange={(e) => handleFieldChange('coverBackgroundColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Color de Texto de Portada</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.coverTextColor || '#ffffff'}
                                                onChange={(e) => handleFieldChange('coverTextColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase font-monospace"
                                                value={themeFormState.coverTextColor || '#ffffff'}
                                                onChange={(e) => handleFieldChange('coverTextColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    {themeFormState.coverBackgroundType === 'image' && (
                                        <div className="col-12">
                                            <DropzoneField
                                                label="Imagen de Fondo de Portada"
                                                fieldName="coverBackgroundUrl"
                                                category="background"
                                                value={themeFormState.coverBackgroundUrl}
                                                onUpload={handleSingleFileUpload}
                                                onRemove={handleRemoveMediaField}
                                                isUploading={activeUploadFieldKey === 'coverBackgroundUrl'}
                                                helpText="Resolución sugerida 1920x1080 (JPG, PNG, WebP)"
                                                aspectHint="1920x1080 (16:9)"
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ============================================================ */}
                            {/* PESTAÑA 2: ESCENARIO, CUERDA Y LODO                         */}
                            {/* ============================================================ */}
                            {activeTabKey === 'game' && (
                                <div className="row g-3">
                                    <div className="col-12">
                                        <h6 className="fw-bold text-primary mb-2">
                                            <i className="bi bi-tv me-1"></i> Fondo y Textos del Escenario de Juego (/screen)
                                        </h6>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Tipo de Fondo del Escenario</label>
                                        <select
                                            className="form-select"
                                            value={themeFormState.gameBackgroundType || 'color'}
                                            onChange={(e) => handleFieldChange('gameBackgroundType', e.target.value)}
                                        >
                                            <option value="color">Color Sólido</option>
                                            <option value="image">Imagen / Estadio</option>
                                        </select>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Color de Fondo del Escenario</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.gameBackgroundColor || '#020617'}
                                                onChange={(e) => handleFieldChange('gameBackgroundColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase font-monospace"
                                                value={themeFormState.gameBackgroundColor || '#020617'}
                                                onChange={(e) => handleFieldChange('gameBackgroundColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Color de Texto del Juego</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.gameTextColor || '#ffffff'}
                                                onChange={(e) => handleFieldChange('gameTextColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase font-monospace"
                                                value={themeFormState.gameTextColor || '#ffffff'}
                                                onChange={(e) => handleFieldChange('gameTextColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    {themeFormState.gameBackgroundType === 'image' && (
                                        <div className="col-12">
                                            <DropzoneField
                                                label="Imagen de Fondo del Escenario de Juego"
                                                fieldName="gameBackgroundUrl"
                                                category="background"
                                                value={themeFormState.gameBackgroundUrl}
                                                onUpload={handleSingleFileUpload}
                                                onRemove={handleRemoveMediaField}
                                                isUploading={activeUploadFieldKey === 'gameBackgroundUrl'}
                                                helpText="Resolución óptima 1920x1080 horizontal para pantalla gigante"
                                                aspectHint="1920x1080 (16:9)"
                                            />
                                        </div>
                                    )}

                                    <div className="col-12">
                                        <hr className="my-2" />
                                        <h6 className="fw-bold text-warning mb-2">
                                            <i className="bi bi-arrows-expand me-1"></i> Textura de Cuerda y Charco de Lodo
                                        </h6>
                                    </div>

                                    <div className="col-md-6">
                                        <DropzoneField
                                            label="Textura / Sprite de la Cuerda"
                                            fieldName="ropeTextureUrl"
                                            category="rope"
                                            value={themeFormState.ropeTextureUrl}
                                            onUpload={handleSingleFileUpload}
                                            onRemove={handleRemoveMediaField}
                                            isUploading={activeUploadFieldKey === 'ropeTextureUrl'}
                                            helpText="Textura horizontal repetible o segmento de soga (PNG transparente)"
                                            aspectHint="PNG Transparente"
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <DropzoneField
                                            label="Sprite / Gráfico del Charco de Lodo Central"
                                            fieldName="mudPuddleUrl"
                                            category="mud"
                                            value={themeFormState.mudPuddleUrl}
                                            onUpload={handleSingleFileUpload}
                                            onRemove={handleRemoveMediaField}
                                            isUploading={activeUploadFieldKey === 'mudPuddleUrl'}
                                            helpText="Gráfico del charco central donde caen los perdedores (PNG transparente)"
                                            aspectHint="PNG Transparente"
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Color del Charco de Lodo (Fallback)</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.mudPuddleColor || '#451a03'}
                                                onChange={(e) => handleFieldChange('mudPuddleColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase font-monospace"
                                                value={themeFormState.mudPuddleColor || '#451a03'}
                                                onChange={(e) => handleFieldChange('mudPuddleColor', e.target.value)}
                                            />
                                        </div>
                                        <small className="text-muted">Color de renderizado en caso de no proveer textura gráfica.</small>
                                    </div>
                                </div>
                            )}

                            {/* ============================================================ */}
                            {/* PESTAÑA 3: EQUIPO 1 (IZQUIERDA)                              */}
                            {/* ============================================================ */}
                            {activeTabKey === 'team1' && (
                                <div className="row g-3">
                                    <div className="col-12">
                                        <div className="p-3 bg-info-subtle border border-info rounded-3 mb-2 d-flex justify-content-between align-items-center">
                                            <h6 className="fw-bold text-info-emphasis m-0">
                                                <i className="bi bi-shield-fill me-1"></i> Configuración y Avatares por Fase: Equipo 1 (Lado Izquierdo)
                                            </h6>
                                            <span className="badge bg-info text-dark">Lado Azul / Izquierda</span>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Nombre del Equipo 1</label>
                                        <input
                                            type="text"
                                            className="form-control fw-bold"
                                            placeholder="EQUIPO 1"
                                            value={themeFormState.player1Name || ''}
                                            onChange={(e) => handleFieldChange('player1Name', e.target.value)}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Color Distintivo del Equipo 1</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.player1Color || '#0284c7'}
                                                onChange={(e) => handleFieldChange('player1Color', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase font-monospace"
                                                value={themeFormState.player1Color || '#0284c7'}
                                                onChange={(e) => handleFieldChange('player1Color', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <DropzoneField
                                            label="Fase 1: Energía Alta (67% - 100%)"
                                            fieldName="player1Phase1Avatar"
                                            category="avatar"
                                            value={themeFormState.player1Phase1Avatar}
                                            onUpload={handleSingleFileUpload}
                                            onRemove={handleRemoveMediaField}
                                            isUploading={activeUploadFieldKey === 'player1Phase1Avatar'}
                                            helpText="Sprite con postura de fuerza máxima y energía (PNG)"
                                            aspectHint="Fase Normal"
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <DropzoneField
                                            label="Fase 2: Esfuerzo Medio (34% - 66%)"
                                            fieldName="player1Phase2Avatar"
                                            category="avatar"
                                            value={themeFormState.player1Phase2Avatar}
                                            onUpload={handleSingleFileUpload}
                                            onRemove={handleRemoveMediaField}
                                            isUploading={activeUploadFieldKey === 'player1Phase2Avatar'}
                                            helpText="Sprite con gesto de sudor / tensión media (PNG)"
                                            aspectHint="Fase Sudor"
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <DropzoneField
                                            label="Fase 3: Al Borde del Lodo (1% - 33%)"
                                            fieldName="player1Phase3Avatar"
                                            category="avatar"
                                            value={themeFormState.player1Phase3Avatar}
                                            onUpload={handleSingleFileUpload}
                                            onRemove={handleRemoveMediaField}
                                            isUploading={activeUploadFieldKey === 'player1Phase3Avatar'}
                                            helpText="Sprite con gesto de agotamiento y resbalón (PNG)"
                                            aspectHint="Fase Agotamiento"
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <DropzoneField
                                            label="Fase 4 (Derrota): Caída en el Lodo"
                                            fieldName="player1LoserAvatar"
                                            category="avatar"
                                            value={themeFormState.player1LoserAvatar}
                                            onUpload={handleSingleFileUpload}
                                            onRemove={handleRemoveMediaField}
                                            isUploading={activeUploadFieldKey === 'player1LoserAvatar'}
                                            helpText="Sprite del equipo derrotado salpicado o en el lodo (PNG)"
                                            aspectHint="Loser Sprite"
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <DropzoneField
                                            label="Fase 4 (Victoria): Festejo y Triunfo"
                                            fieldName="player1WinnerAvatar"
                                            category="avatar"
                                            value={themeFormState.player1WinnerAvatar}
                                            onUpload={handleSingleFileUpload}
                                            onRemove={handleRemoveMediaField}
                                            isUploading={activeUploadFieldKey === 'player1WinnerAvatar'}
                                            helpText="Sprite del equipo celebrando la victoria (PNG)"
                                            aspectHint="Winner Sprite"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* ============================================================ */}
                            {/* PESTAÑA 4: EQUIPO 2 (DERECHA)                                */}
                            {/* ============================================================ */}
                            {activeTabKey === 'team2' && (
                                <div className="row g-3">
                                    <div className="col-12">
                                        <div className="p-3 bg-danger-subtle border border-danger rounded-3 mb-2 d-flex justify-content-between align-items-center">
                                            <h6 className="fw-bold text-danger-emphasis m-0">
                                                <i className="bi bi-shield-fill me-1"></i> Configuración y Avatares por Fase: Equipo 2 (Lado Derecho)
                                            </h6>
                                            <span className="badge bg-danger">Lado Rojo / Derecha</span>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Nombre del Equipo 2</label>
                                        <input
                                            type="text"
                                            className="form-control fw-bold"
                                            placeholder="EQUIPO 2"
                                            value={themeFormState.player2Name || ''}
                                            onChange={(e) => handleFieldChange('player2Name', e.target.value)}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Color Distintivo del Equipo 2</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.player2Color || '#ef4444'}
                                                onChange={(e) => handleFieldChange('player2Color', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase font-monospace"
                                                value={themeFormState.player2Color || '#ef4444'}
                                                onChange={(e) => handleFieldChange('player2Color', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <DropzoneField
                                            label="Fase 1: Energía Alta (67% - 100%)"
                                            fieldName="player2Phase1Avatar"
                                            category="avatar"
                                            value={themeFormState.player2Phase1Avatar}
                                            onUpload={handleSingleFileUpload}
                                            onRemove={handleRemoveMediaField}
                                            isUploading={activeUploadFieldKey === 'player2Phase1Avatar'}
                                            helpText="Sprite con postura de fuerza máxima y energía (PNG)"
                                            aspectHint="Fase Normal"
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <DropzoneField
                                            label="Fase 2: Esfuerzo Medio (34% - 66%)"
                                            fieldName="player2Phase2Avatar"
                                            category="avatar"
                                            value={themeFormState.player2Phase2Avatar}
                                            onUpload={handleSingleFileUpload}
                                            onRemove={handleRemoveMediaField}
                                            isUploading={activeUploadFieldKey === 'player2Phase2Avatar'}
                                            helpText="Sprite con gesto de sudor / tensión media (PNG)"
                                            aspectHint="Fase Sudor"
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <DropzoneField
                                            label="Fase 3: Al Borde del Lodo (1% - 33%)"
                                            fieldName="player2Phase3Avatar"
                                            category="avatar"
                                            value={themeFormState.player2Phase3Avatar}
                                            onUpload={handleSingleFileUpload}
                                            onRemove={handleRemoveMediaField}
                                            isUploading={activeUploadFieldKey === 'player2Phase3Avatar'}
                                            helpText="Sprite con gesto de agotamiento y resbalón (PNG)"
                                            aspectHint="Fase Agotamiento"
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <DropzoneField
                                            label="Fase 4 (Derrota): Caída en el Lodo"
                                            fieldName="player2LoserAvatar"
                                            category="avatar"
                                            value={themeFormState.player2LoserAvatar}
                                            onUpload={handleSingleFileUpload}
                                            onRemove={handleRemoveMediaField}
                                            isUploading={activeUploadFieldKey === 'player2LoserAvatar'}
                                            helpText="Sprite del equipo derrotado salpicado o en el lodo (PNG)"
                                            aspectHint="Loser Sprite"
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <DropzoneField
                                            label="Fase 4 (Victoria): Festejo y Triunfo"
                                            fieldName="player2WinnerAvatar"
                                            category="avatar"
                                            value={themeFormState.player2WinnerAvatar}
                                            onUpload={handleSingleFileUpload}
                                            onRemove={handleRemoveMediaField}
                                            isUploading={activeUploadFieldKey === 'player2WinnerAvatar'}
                                            helpText="Sprite del equipo celebrando la victoria (PNG)"
                                            aspectHint="Winner Sprite"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* ============================================================ */}
                            {/* PESTAÑA 5: VISTA CELULAR AFICIONADO                          */}
                            {/* ============================================================ */}
                            {activeTabKey === 'fan' && (
                                <div className="row g-3">
                                    <div className="col-12">
                                        <div className="p-3 bg-light border rounded-3 mb-2">
                                            <h6 className="fw-bold text-dark m-0">
                                                <i className="bi bi-phone me-1"></i> Personalización de la Pantalla del Aficionado Móvil (/tugofwar)
                                            </h6>
                                            <small className="text-muted">
                                                Personaliza el fondo y colores de texto que ven los aficionados en sus teléfonos durante la votación de equipo y la partida.
                                            </small>
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Tipo de Fondo Móvil</label>
                                        <select
                                            className="form-select"
                                            value={themeFormState.fanBackgroundType || 'color'}
                                            onChange={(e) => handleFieldChange('fanBackgroundType', e.target.value)}
                                        >
                                            <option value="color">Color Sólido</option>
                                            <option value="image">Imagen Vertical</option>
                                        </select>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Color de Fondo Móvil</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.fanBackgroundColor || '#0f172a'}
                                                onChange={(e) => handleFieldChange('fanBackgroundColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase font-monospace"
                                                value={themeFormState.fanBackgroundColor || '#0f172a'}
                                                onChange={(e) => handleFieldChange('fanBackgroundColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Color de Texto Móvil</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.fanTextColor || '#ffffff'}
                                                onChange={(e) => handleFieldChange('fanTextColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase font-monospace"
                                                value={themeFormState.fanTextColor || '#ffffff'}
                                                onChange={(e) => handleFieldChange('fanTextColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    {themeFormState.fanBackgroundType === 'image' && (
                                        <div className="col-12">
                                            <DropzoneField
                                                label="Imagen de Fondo para Aficionado Móvil"
                                                fieldName="fanBackgroundUrl"
                                                category="background"
                                                value={themeFormState.fanBackgroundUrl}
                                                onUpload={handleSingleFileUpload}
                                                onRemove={handleRemoveMediaField}
                                                isUploading={activeUploadFieldKey === 'fanBackgroundUrl'}
                                                helpText="Resolución sugerida 1080x1920 vertical para celulares"
                                                aspectHint="1080x1920 (9:16)"
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Modal Footer Actions */}
                        <div className="d-flex justify-content-between align-items-center p-3 px-4 bg-light border-top">
                            <button
                                type="button"
                                className="btn btn-outline-secondary fw-bold px-4"
                                onClick={handleClose}
                                disabled={isSavingInProgress}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="btn btn-success fw-bold px-4 shadow-sm"
                                disabled={isSavingInProgress}
                            >
                                {isSavingInProgress ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Guardando...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check-circle-fill me-1"></i>
                                        {themeFormState.id > 0 ? 'Actualizar Tema' : 'Guardar Tema'}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

TugOfWarThemeModal.propTypes = {
    isOpen: PropTypes.bool,
    show: PropTypes.bool,
    onCloseModal: PropTypes.func,
    onClose: PropTypes.func,
    initialThemeData: PropTypes.object,
    themeToEdit: PropTypes.object,
    onThemeSavedSuccessfully: PropTypes.func,
    onThemeSaved: PropTypes.func,
    onSave: PropTypes.func,
    roomId: PropTypes.string,
    operatorId: PropTypes.string
};

export default TugOfWarThemeModal;
